'use client'

import {useState, useEffect, useRef, RefObject} from 'react'
import useDebouncedCallback from './useDebouncedCallback'
import useWindowResize from './useWindowResize'
import {useHeaderStore} from '@local/stores/stores'

const useBlocksObserver = (ref: RefObject<HTMLDivElement>): boolean => {
	const [isDark, setIsDark] = useState(false)
	const intersectionObserver = useRef<IntersectionObserver>()
	const blocks = useHeaderStore(state => state.blocks)
	const intersectedBlocks = useRef([])
	const innerWidth = useRef(0)
	const refBoundingClientRect = useRef<DOMRect>()

	useEffect(() => {
		intersectedBlocks.current = []
		intersectionObserver.current?.disconnect()
		blocks.forEach((block) => {
			intersectionObserver.current?.observe(block)
		})
	}, [blocks])

	useEffect(() => {
		return () => {
			if (intersectionObserver.current) {
				intersectionObserver.current.disconnect()
			}
		}
	}, [])

	useWindowResize(e => {
		if (intersectionObserver.current) intersectionObserver.current.disconnect()
		if (!ref.current) return

		if (e.innerWidth !== innerWidth.current) {
			refBoundingClientRect.current = ref.current.getBoundingClientRect()
		}

		innerWidth.current = e.innerWidth!

		const height = refBoundingClientRect.current!.height
		const top = refBoundingClientRect.current!.top
		const rootMargin = `${Math.round(-top - height / 2)}px -50% ${Math.round(top + height / 2 - e.innerHeight! + 1)}px -50%`
		intersectionObserver.current = new IntersectionObserver(handleIntersect, {rootMargin})
		blocks.forEach(block => {
			intersectionObserver.current?.observe(block)
		})
	}, true, [blocks], 30)

	const debouncedIntersect = useDebouncedCallback(() => {
		let isDark = false
		intersectedBlocks.current.forEach(() => {
			isDark = true
		})
		setIsDark(isDark)
	}, 30, [])

	const handleIntersect = (entries, observer) => {
		entries.forEach(entry => {
			const index = intersectedBlocks.current.indexOf(entry.target)
			if (entry.isIntersecting && index === -1) {
				intersectedBlocks.current.push(entry.target)
			} else if (!entry.isIntersecting && index !== -1) {
				intersectedBlocks.current.splice(index, 1)
			}
		})

		debouncedIntersect()
	}

	return isDark
}

export default useBlocksObserver
